import { Controller } from "@hotwired/stimulus"
import { inlineTaskResult } from "../lib/inline_task_form_state.mjs"

// Saves one task property in place. The server remains authoritative: the
// control keeps an unsaved value on failure and only advances its baseline
// after a successful response.
export default class extends Controller {
  static targets = ["input", "status"]
  static values = { reload: Boolean }

  connect() {
    this.savedValue = this.inputTarget.value
  }

  stop(event) {
    event.stopPropagation()
  }

  keydown(event) {
    event.stopPropagation()

    if (event.key === "Escape") {
      event.preventDefault()
      this.inputTarget.value = this.savedValue
      this.inputTarget.blur()
      this.renderState("idle", "Change cancelled.")
    } else if (event.key === "Enter" && this.inputTarget.tagName !== "SELECT") {
      event.preventDefault()
      this.save(event)
    }
  }

  async save(event) {
    event?.preventDefault()
    event?.stopPropagation()
    if (this.saving || this.inputTarget.value === this.savedValue) return

    this.saving = true
    this.element.setAttribute("aria-busy", "true")
    const formData = new FormData(this.element)
    this.inputTarget.disabled = true
    this.renderState("saving", "Saving…")

    try {
      const response = await fetch(this.element.action, {
        method: "PATCH",
        body: formData,
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-Inline-Task-Update": "true"
        }
      })
      const payload = await response.json()
      if (!response.ok && response.status !== 422) throw new Error(`HTTP ${response.status}`)

      const result = inlineTaskResult(response.ok, payload)
      this.renderState(result.tone, result.message)

      if (response.ok) {
        this.savedValue = this.inputTarget.value
        this.dispatch("saved", { detail: { task: payload.task } })
        if (this.reloadValue) this.refreshCurrentView()
      }
    } catch (_) {
      const result = inlineTaskResult(false)
      this.renderState(result.tone, result.message)
    } finally {
      this.saving = false
      this.element.removeAttribute("aria-busy")
      this.inputTarget.disabled = false
    }
  }

  renderState(state, message) {
    this.element.dataset.state = state
    this.statusTarget.textContent = message
  }

  refreshCurrentView() {
    if (window.Turbo?.visit) {
      window.Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.location.reload()
    }
  }
}
